import { Button, Form, Input, InputNumber, Select, Space } from "@arco-design/web-react";
import { useEffect, useState } from "react";
import { copy } from "../utils/copy";
import { IconCopy } from "@arco-design/web-react/icon";
import { toHValue } from "../utils/wrap";

const Option = Select.Option;
const FormItem = Form.Item;
const LinearSpeedCalculation = () => {
  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm();
  const STORAGE_KEY = "tc_values_v1";
  const loadSaved = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };
  const save = (data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
  };
  const calculate = () => {
    switch (current) {
      case 6: // 已知 X，角度θ，求 Z  => Z = X / 2 / tanθ
        const { x6, a6 } = form.getFieldsValue();
        form.setFieldValue("Z", toHValue(x6 / 2 / Math.tan((a6 * Math.PI) / 180)));
        break;
      case 7: // 已知 Z，角度θ，求 X  => X = 2 * Z * tanθ
        const { Z7, a7 } = form.getFieldsValue();
        form.setFieldValue("X", toHValue(2 * Z7 * Math.tan((a7 * Math.PI) / 180)));
        break;
      case 0:
        const { D0, d0, t0 } = form.getFieldsValue();
        form.setFieldValue("Z", toHValue(t0 * (D0 - d0)));
        break;
      case 1:
        const { D1, d1, a1 } = form.getFieldsValue();
        form.setFieldValue("Z", toHValue((D1 - d1) / 2 / Math.tan((a1 * Math.PI) / 180)));
        break;
      case 2:
        const { D2, Z2, t2 } = form.getFieldsValue();
        form.setFieldValue("d", toHValue(D2 - Z2 / t2));
        break;
      case 3:
        const { D3, Z3, a3 } = form.getFieldsValue();
        form.setFieldValue("d", toHValue(D3 - 2 * Z3 * Math.tan((a3 * Math.PI) / 180)));
        break;
      case 4:
        const { d4, Z4, t4 } = form.getFieldsValue();
        form.setFieldValue("D", toHValue(d4 + Z4 / t4));
        break;
      case 5:
        const { d5, Z5, a5 } = form.getFieldsValue();
        form.setFieldValue("D", toHValue(d5 + 2 * Z5 * Math.tan((a5 * Math.PI) / 180)));
        break;
      default:
        break;
    }
    // persist after calculation
    save({ current, values: form.getFieldsValue() });
  };
  const options = [
    { name: "已知 X，角度θ，求 Z", value: 6 },
    { name: "已知 Z，角度θ，求 X", value: 7 },
    { name: "已知大径、小径、锥度，求高度", value: 0 },
    {
      name: "已知大径、小径、角度，求高度",
      value: 1,
    },
    { name: "已知大径、高度、锥度，求小径", value: 2 },
    {
      name: "已知大径、高度、角度，求小径",
      value: 3,
    },
    { name: "已知小径、高度、锥度，求大径", value: 4 },
    { name: "已知小径、高度、角度，求大径", value: 5 },
  ];

  useEffect(() => {
    const saved = loadSaved();
    if (saved) {
      if (typeof saved.current === "number") setCurrent(saved.current);
      if (saved.values) form.setFieldsValue(saved.values);
    }
  }, []);

  return (
    <div
      className="cpn-wrapper-horizontal"
      style={{
        width: "90%",
        margin: "0 auto",
      }}
    >
      <Form
        style={{ maxWidth: 400, width: "90%%" }}
        autoComplete="off"
        form={form}
        colon=":"
        onSubmit={calculate}
        layout={document.body.clientWidth <= 780 ? "vertical" : "horizontal"}
        onValuesChange={(_, allValues) => {
          save({ current, values: allValues });
        }}
      >
        <FormItem label="类型" field="type">
          <Select
            size="large"
            placeholder="请选择已知条件"
            onChange={(value) => {
              setCurrent(value);
              save({ current: value, values: form.getFieldsValue() });
            }}
            value={current}
          >
            {options.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.name}
              </Option>
            ))}
          </Select>
        </FormItem>
        {current === 6 && (
          <>
            <FormItem
              label="直径差X"
              field="x6"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="角度θ"
              field="a6"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
          </>
        )}
        {current === 7 && (
          <>
            <FormItem
              label="高度Z"
              field="Z7"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="角度θ"
              field="a7"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
          </>
        )}
        {current === 0 && (
          <>
            <FormItem
              label="大径D"
              field="D0"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="小径d"
              field="d0"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="锥度1:n"
              field="t0"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
          </>
        )}
        {current === 1 && (
          <>
            <FormItem
              label="大径D"
              field="D1"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="小径d"
              field="d1"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="角度θ"
              field="a1"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
          </>
        )}
        {current === 2 && (
          <>
            <FormItem
              label="大径D"
              field="D2"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="高度Z"
              field="Z2"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="锥度1:n"
              field="t2"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
          </>
        )}
        {current === 3 && (
          <>
            <FormItem
              label="大径D"
              field="D3"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="高度Z"
              field="Z3"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="角度θ"
              field="a3"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
          </>
        )}
        {current === 4 && (
          <>
            <FormItem
              label="小径d"
              field="d4"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="高度Z"
              field="Z4"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="锥度1:n"
              field="t4"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
          </>
        )}
        {current === 5 && (
          <>
            <FormItem
              label="小径d"
              field="d5"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="高度Z"
              field="Z5"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
            <FormItem
              label="角度θ"
              field="a5"
              rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
            >
              <InputNumber size="large" />
            </FormItem>
          </>
        )}
        <FormItem wrapperCol={{ offset: 5 }}>
          <Space>
            <Button type="primary" htmlType="submit">
              立即计算
            </Button>
            <Button onClick={() => { form.resetFields(); localStorage.removeItem(STORAGE_KEY); }}>重置</Button>
          </Space>
        </FormItem>
        {(current === 6 || current === 0 || current === 1) && (
          <FormItem label="高度Z" field="Z">
            <Input
              size="large"
              readOnly
              onClick={copy}
              id="result_tc_Z01"
              placeholder="计算结果在此次显示"
              suffix={<IconCopy onClick={copy.bind(this, null, "#result_tc_Z01")} style={{ cursor: "pointer" }} />}
            />
          </FormItem>
        )}
        {current === 7 && (
          <FormItem label="直径差X" field="X">
            <Input
              size="large"
              readOnly
              onClick={copy}
              id="result_tc_X7"
              placeholder="计算结果在此次显示"
              suffix={<IconCopy onClick={copy.bind(this, null, "#result_tc_X7")} style={{ cursor: "pointer" }} />}
            />
          </FormItem>
        )}
        {(current === 2 || current === 3) && (
          <FormItem label="小径d" field="d">
            <Input
              size="large"
              readOnly
              onClick={copy}
              id="result_tc_d23"
              placeholder="计算结果在此次显示"
              suffix={<IconCopy onClick={copy.bind(this, null, "#result_tc_d23")} style={{ cursor: "pointer" }} />}
            />
          </FormItem>
        )}
        {(current === 4 || current === 5) && (
          <FormItem label="大径D" field="D">
            <Input
              size="large"
              readOnly
              onClick={copy}
              id="result_tc_D45"
              placeholder="计算结果在此次显示"
              suffix={<IconCopy onClick={copy.bind(this, null, "#result_tc_D45")} style={{ cursor: "pointer" }} />}
            />
          </FormItem>
        )}
      </Form>
    </div>
  );
};
export default LinearSpeedCalculation;
